import { MultiselectProps, SelectProps } from '@amzn/awsui-components-react-v3';

import { RFXLineItem } from '../interfaces/RFXLineItemModel';
import { Contact, DocumentEntry, QuoteEntry } from '.';

/**
 * Life cycle graphs for RFXResponse entity
 * Backend ref: https://code.amazon.com/packages/AWSSCMRFXService/blobs/mainline/--/src/com/aws/scm/rfxresponsesvc/domain/view/dao/RFXResponseLifeCycleGraphDao.java
 */
export enum RFXResponseState {
  DRAFT = 'Draft',
  ACCEPTED = 'Accepted',
  REJECTED = 'Rejected',
  SUBMITTED = 'Submitted',
  SOFT_AWARDED = 'Soft_Awarded',
  AWARDED = 'Awarded',
  PARTIALLY_AWARDED = 'Partially_Awarded',
  ARCHIVED = 'Archived',
  DECLINED = 'Declined',
  DISCARDED = 'Discarded',
  RECALLED = 'Recalled'
}

export interface RFXResponseGeneralInformation {
  requestId: string;
  responseId?: string;
  rfxType: string;
  rfxName: string;
  beginDate: string;
  dueDate: string;
  needByDate: string;
  region: SelectProps.Option;
  regions?: MultiselectProps.Options;
  currency: SelectProps.Option;
  quoteTemplateType?: SelectProps.Option;
  rfxLineItemClassId: string;
  defaultQuoteValidity: number | undefined;
  overview: string;
  processDetails: string;
}

export interface RFXResponseQuotes {
  quoteValidity: number | undefined;
  requestQuote: QuoteEntry;
  quoteEntries: QuoteEntry[];
}

export interface RFXResponseReview {
  // No data
}

export interface RFXResponseDocuments {
  requestDocuments: DocumentEntry[];
  documentEntries: DocumentEntry[];
}

export interface Contacts {
  contacts: Contact[];
}

export interface RFXResponseForm {
  state: RFXResponseState;
  round: number;
  generalInformation: RFXResponseGeneralInformation;
  quotes: RFXResponseQuotes;
  documents: RFXResponseDocuments;
  contacts: Contacts;
  review: RFXResponseReview;
}

export type ResponseFormKey = 'generalInformation' | 'contacts' | 'quotes' | 'documents' | 'review';

export type ResponseOnChangeParam<K extends ResponseFormKey> = Partial<Pick<RFXResponseForm, K>[K]>;

export type ResponseOnChangeFn<K extends ResponseFormKey> = (data: ResponseOnChangeParam<K>) => void;

export enum RFXResponseAction {
  INITIATE_RESPONSE,
  EDIT_RESPONSE
}

/**
 * Backend model that contains data useful for both supplier details page
 * and supplier workflow.
 */
export interface RFXSupplierBackendModel {
  round: number;
  requestId: string;
  requestBusinessId: string;
  workflow?: string;
  rfxType: string;
  rfxName: string;
  buyer: string;
  requestStatus: string;
  beginDate: string;
  dueDate: string;
  needByDate: string;
  ingestionId?: string;
  decisionTime?: number;
  region: string;
  regions?: string[];
  currency: string;
  quoteTemplateType?: string;
  rfxLineItemClassId: string;
  overview: string;
  processDetails: string;
  supplier: string;
  daysRemaining: number;
  defaultQuoteValidity?: number;
  quoteValidity?: number;
  responseId: string;
  responseRevision: number;
  responseVersion: number;
  responseStatus: string;
  requestQuote: QuoteEntry;
  responseQuotes: QuoteEntry[];
  requestDocuments: DocumentEntry[];
  responseDocuments: DocumentEntry[];
  contacts: Contact[];
  totalAcquisitionCost: number | null;
  incoterm?: string;
  lineItems: RFXLineItem[];
}

export interface RFXSupplierBackendModelList {
  rfxEvent: RFXSupplierBackendModel[];
}
